// Role-based permissions helper.
//
// The current user's permissions are fetched once (right after login, or on a hard
// reload via ensurePermissionsLoaded) and cached in localStorage alongside authToken/
// userRole. Gating reads are synchronous through `can(module, operation)`.
//
// Delete is globally disabled for now: `can(..., "canDelete")` always returns false,
// and the admin matrix renders the Delete column greyed/read-only.

import apis from "./api";

export const MODULES = ["Customer", "Product", "Combo", "Order", "Invoice"];

// Fallback operation set; the admin screen prefers GET /permissions/modules when available.
export const OPERATIONS = [
  { key: "canCreate", label: "Create", enabled: true },
  { key: "canView", label: "View", enabled: true },
  { key: "canUpdate", label: "Update", enabled: true },
  { key: "canEditProductDescription", label: "Edit Product Description", enabled: true },
  { key: "canDelete", label: "Delete", enabled: false },
];

const IS_ADMIN_KEY = "isAdmin";
const PERMISSIONS_KEY = "permissions";

// Fetch GET /permissions/me and cache isAdmin + per-module flags. Call after login.
export async function loadMyPermissions() {
  const data = await apis.getMyPermissions();
  localStorage.setItem(IS_ADMIN_KEY, JSON.stringify(!!data.isAdmin));
  localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(data.permissions || []));
  return data;
}

// Load permissions if we have a token but no cached permissions yet (e.g. an existing
// session from before this feature shipped, or a cleared cache). Returns true if it
// actually fetched, so the caller can force a one-time reload to re-render gating.
export async function ensurePermissionsLoaded() {
  const hasToken = !!localStorage.getItem("authToken");
  const hasCache = localStorage.getItem(PERMISSIONS_KEY) !== null;
  if (hasToken && !hasCache) {
    try {
      await loadMyPermissions();
      return true;
    } catch (e) {
      // Leave the cache empty; gating will default to "no access" until next login.
    }
  }
  return false;
}

export function getIsAdmin() {
  try {
    return JSON.parse(localStorage.getItem(IS_ADMIN_KEY) || "false");
  } catch (e) {
    return false;
  }
}

export function getPermissions() {
  try {
    return JSON.parse(localStorage.getItem(PERMISSIONS_KEY) || "[]");
  } catch (e) {
    return [];
  }
}

export function clearPermissions() {
  localStorage.removeItem(IS_ADMIN_KEY);
  localStorage.removeItem(PERMISSIONS_KEY);
}

// Can the current user perform `operation` on `module`?
// Admins can do everything except Delete (globally disabled for now).
export function can(module, operation) {
  if (operation === "canDelete") return false; // Delete disabled globally
  if (getIsAdmin()) return true;
  const row = getPermissions().find((p) => p.module === module);
  return !!(row && row[operation]);
}

// Convenience helpers.
export const canView = (module) => can(module, "canView");
export const canCreate = (module) => can(module, "canCreate");
export const canUpdate = (module) => can(module, "canUpdate");
export const canDelete = (module) => {
  // Delete is allowed for admins or users with explicit canDelete permission
  if (getIsAdmin()) return true;
  const row = getPermissions().find((p) => p.module === module);
  return !!(row && row.canDelete);
};
